import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SharedSetlistView from '../components/SharedSetlistView';
import StandardPageHeader from '../components/StandardPageHeader';
import { Button } from "../components/ui/button";
import { Loader2, Plus, ArrowUpDown, ExternalLink, Edit } from "lucide-react";
import logger from '../services/LoggingService';
import { getSetlistById, updateSetlist } from '../services/SetlistStorageService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import bandService from '../services/BandService';
import eventBus from '../utils/EventBus';
import useUnsavedChanges from '../hooks/useUnsavedChanges';

/**
 * Page component for viewing a shared setlist.
 * This is a wrapper around the existing SharedSetlistView component
 * to integrate it with our new design system.
 */
const SetlistViewPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [setlist, setSetlist] = useState(null);
  const [error, setError] = useState(null);
  
  // Edit modal state
  const [editingSetlist, setEditingSetlist] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    band_id: '',
    isPublic: true
  });
  const [userBands, setUserBands] = useState([]);
  
  // Get auth and notification contexts
  const { isAuthenticated, currentUser } = useAuth();
  const { showNotification } = useNotifications();
  
  // State for tracking unsaved changes in SharedSetlistView
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hook to warn about unsaved changes when navigating away
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    hasUnsavedChanges,
    'You have unsaved changes to this setlist. Are you sure you want to leave?'
  );

  // Directly load the setlist data here to debug issues
  useEffect(() => {
    async function loadSetlist() {
      if (!id) {
        setError("No setlist ID provided");
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Log ID for debugging
        console.log('SetlistViewPage - Loading setlist with ID:', id);
        logger.debug('SetlistViewPage', 'Loading setlist with ID:', id);
        
        // Try to load the setlist
        const data = await getSetlistById(id);
        console.log('SetlistViewPage - API Response:', data);
        
        if (data) {
          setSetlist(data);
        } else {
          setError(`Setlist not found with ID: ${id}`);
        }
      } catch (err) {
        console.error('Error loading setlist:', err);
        setError(`Error loading setlist: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    }
    
    loadSetlist();
  }, [id]);

  // Load user bands when authenticated
  useEffect(() => {
    const loadUserBands = async () => {
      if (isAuthenticated) {
        try {
          const bands = await bandService.getUserBands();
          setUserBands(bands);
        } catch (error) {
          console.error('Error loading user bands:', error);
        }
      } else {
        setUserBands([]);
      }
    };
    
    loadUserBands();
  }, [isAuthenticated]);

  // Set up event listeners for edit and save actions
  useEffect(() => {
    const editListener = eventBus.on('setlist:edit', async () => {
      if (id && setlist) {
        // Open edit modal instead of navigating
        setEditingSetlist(setlist);
        setEditForm({
          name: setlist.name || '',
          description: setlist.description || '',
          band_id: setlist.band_id || '',
          isPublic: setlist.isPublic !== false
        });
      }
    });

    const saveChangesListener = eventBus.on('setlist:saveChanges', async () => {
      if (id && setlist) {
        setIsSaving(true);
        eventBus.emit('setlist:doSaveChanges', setlist);
      }
    });

    const unsavedChangesListener = eventBus.on('setlist:unsavedChangesChanged', (hasChanges) => {
      setHasUnsavedChanges(hasChanges);
      if (!hasChanges) {
        setIsSaving(false);
      }
    });

    return () => {
      editListener();
      saveChangesListener();
      unsavedChangesListener();
    };
  }, [id, setlist]);

  // Set up keyboard shortcut for Cmd+S / Ctrl+S
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+S (Mac) or Ctrl+S (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save behavior
        
        // Only trigger save if there are unsaved changes and we're not already saving
        if (hasUnsavedChanges && !isSaving && id && setlist) {
          setIsSaving(true);
          eventBus.emit('setlist:doSaveChanges', setlist);
        }
      }
    };

    // Add the event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasUnsavedChanges, isSaving, id, setlist]);

  // Handle edit form submission
  const handleEditSetlist = async (e) => {
    e.preventDefault();
    if (!editingSetlist) return;

    try {
      const updatedSetlist = {
        ...editingSetlist,
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        band_id: editForm.band_id || null,
        isPublic: editForm.isPublic
      };

      await updateSetlist(editingSetlist._id, updatedSetlist);

      // Update local setlist state
      setSetlist(updatedSetlist);

      // Close modal
      setEditingSetlist(null);
      setEditForm({ name: '', description: '', band_id: '', isPublic: true });
      
      showNotification('Setlist updated successfully', 'success');
    } catch (error) {
      console.error('Error updating setlist:', error);
      showNotification('Failed to update setlist', 'error');
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-10 flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading setlist...</p>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto py-10">
        <div className="bg-destructive/10 border border-destructive rounded-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Error</h2>
          <p className="mb-4">{error}</p>
          <Link to="/">
            <Button variant="default">Return to Home</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  // Render the setlist view if we have data
  if (setlist) {
    return (
      <div className="max-w-6xl mx-auto">
        {/* Standard page header with back button, title, and actions */}
        <StandardPageHeader
          title={setlist.name}
          backTo="/setlists"
          backLabel="Setlists"
          actions={
            <>
              <button
                onClick={() => eventBus.emit('setlist:addSheet')}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span className="hidden sm:inline">Add Sheet</span>
              </button>
              <button
                onClick={() => eventBus.emit('setlist:toggleReorder')}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
              >
                <ArrowUpDown className="w-3 h-3" />
                <span className="hidden sm:inline">Reorder</span>
              </button>
              <button
                onClick={() => eventBus.emit('setlist:openAll')}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                <span className="hidden sm:inline">Open All</span>
              </button>
              <button
                onClick={() => eventBus.emit('setlist:edit')}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
              >
                <Edit className="w-3 h-3" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button
                onClick={() => eventBus.emit('setlist:saveChanges')}
                className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${
                  hasUnsavedChanges
                    ? 'text-foreground hover:bg-muted'
                    : 'text-muted-foreground cursor-not-allowed'
                }`}
                disabled={isSaving || !hasUnsavedChanges}
                title={hasUnsavedChanges ? 'Save your changes' : 'No unsaved changes'}
                id="setlist-save-button"
              >
                {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
              </button>
            </>
          }
        />

        <SharedSetlistView id={id} setlistData={setlist} hideHeader={true} />
        
        {/* Edit Setlist Modal */}
        {editingSetlist && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Edit Setlist</h2>
                <button
                  onClick={() => {
                    setEditingSetlist(null);
                    setEditForm({ name: '', description: '', band_id: '', isPublic: true });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleEditSetlist} className="space-y-4">
                <div>
                  <label htmlFor="setlist-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="setlist-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="setlist-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="setlist-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional description for your setlist"
                  />
                </div>

                <div>
                  <label htmlFor="setlist-band" className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to Band (Optional)
                  </label>
                  <select
                    id="setlist-band"
                    value={editForm.band_id}
                    onChange={(e) => setEditForm({ ...editForm, band_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Personal setlist (no band)</option>
                    {userBands.map(band => (
                      <option key={band._id} value={band._id}>
                        {band.name}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    Personal setlists: Only you can vote • Band setlists: All band members can vote
                  </div>
                </div>

                {/* Private toggle - only for premium users */}
                {currentUser?.isPremium && (
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Private
                      </label>
                      <div className="text-xs text-gray-500">
                        Only you and shared users can see this setlist
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, isPublic: !editForm.isPublic })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        !editForm.isPublic ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          !editForm.isPublic ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSetlist(null);
                      setEditForm({ name: '', description: '', band_id: '', isPublic: true });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Unsaved Changes Confirmation Dialog */}
        {showPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-semibold mb-4">Unsaved Changes</h2>
              <p className="text-gray-700 mb-6">
                You have unsaved changes to this setlist. Are you sure you want to leave?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelNavigation}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Stay on Page
                </button>
                <button
                  onClick={confirmNavigation}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium"
                >
                  Leave Without Saving
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default SetlistViewPage;
